import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import PropTypes from 'prop-types';
import React from 'react';
import RightArrowIcon from '@mui/icons-material/ChevronRight';
import Tooltip from '@mui/material/Tooltip';
import {styled} from '@mui/material/styles';
import {useDrag} from 'react-dnd';
import {useTranslation} from 'react-i18next';

import ItemType from '@explorer/components/ui/item-type';
import Thumb from '@explorer/components/ui/thumbnail-stickymap';
import ThumbnailCard from '@explorer/components/ui/thumbnail-card';
import {
  AreaCoverageIndicator,
  IndicatorsContainer,
  PartialRectIndicator,
  PixelResolutionIndicator,
} from '@explorer/components/ui/metadata-indicators';
import {COMPARE_TYPES} from '@explorer/constants';
import {
  ItemsCount,
  ListItem,
  RequestAccessButton,
  THUMBNAIL_DIMENSIONS,
  ItemTitle as Title,
} from './item-components.jsx';
import {getResolution} from '@explorer/util/items';
import {hasPartialRectification} from '@explorer/util/results';

const ButtonStyled = styled(Button)({
  height: '100%',
  width: 30,
  minWidth: 30,
});

const ResultItem = ({
  result,
  geometry,
  isComparing,
  onClick,
  selected,
  setActiveResult,
  showDetails,
  actions,
  onRequestAccess,
}) => {
  const {t} = useTranslation();

  const [{isDragging}, dragRef] = useDrag(
    () => ({
      type: COMPARE_TYPES.RESULT,
      item: result,
      canDrag: isComparing,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [isComparing]
  );

  const items = result.properties.items;
  const resolution = getResolution(items[0]);

  const someUnavailable = items.some(item => !item.properties.available);
  const noneAvailable = items.every(item => !item.properties.available);
  const isAvailable = !(someUnavailable || noneAvailable);

  const handleViewDetails = () => {
    setActiveResult(result);
  };

  return (
    <ListItem
      ref={dragRef}
      divider
      alignItems="center"
      button={!isComparing}
      draggable={isComparing}
      isDragging={isDragging}
      isComparing={isComparing}
      selected={selected}
      onClick={event => !isComparing && onClick(event, result)}
      data-qe="result-list-item"
      data-qe-selected={selected ? 'true' : undefined}
    >
      <ThumbnailCard
        height={THUMBNAIL_DIMENSIONS}
        width={THUMBNAIL_DIMENSIONS}
        marginRight={2}
        selected={selected}
        thumbnail={
          <Thumb
            ariaLabel={`Thumbnail of scenes for ${t('common:date.long', {
              date: result.properties.acquired,
            })}`}
            clip={geometry}
            items={items}
            height={THUMBNAIL_DIMENSIONS}
            width={THUMBNAIL_DIMENSIONS}
          />
        }
      >
        {!isAvailable && (
          <RequestAccessButton
            data-qe="result-item-access-button"
            onClick={onRequestAccess}
          >
            {t('common:purchaseAccess', 'Purchase Access')}
          </RequestAccessButton>
        )}
      </ThumbnailCard>
      <ListItemText disableTypography>
        <Box mb={1.5}>
          <ItemsCount
            selected={0} // TODO: Implement after refactor structure of selected items state
            total={items.length}
            suffix="items"
          />
          <Title data-qe="item-date" gutterBottom>
            {t('common:date.long', {
              date: result.properties.acquired,
            })}
          </Title>
          <ItemType
            itemType={result.properties.item_type}
            data-qe="item-type-label"
          />
        </Box>

        <IndicatorsContainer>
          <PixelResolutionIndicator resolution={resolution} />
          {result.properties.coverage !== undefined && (
            <AreaCoverageIndicator coverage={result.properties.coverage} />
          )}
          {hasPartialRectification(result.properties.items) && (
            <PartialRectIndicator data-qe="partial-rectification" />
          )}
          <Box mb={1}>{actions && actions}</Box>
        </IndicatorsContainer>
      </ListItemText>

      <ListItemSecondaryAction sx={{height: '100%', paddingY: 2}}>
        {showDetails && (
          <Tooltip title={t('itemsPanel.tooltipViewDetails')}>
            <ButtonStyled
              color="primary"
              variant="contained"
              onClick={handleViewDetails}
              data-qe="view-details-button"
              size="small"
            >
              <RightArrowIcon />
            </ButtonStyled>
          </Tooltip>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  );
};

ResultItem.propTypes = {
  geometry: PropTypes.object,
  isComparing: PropTypes.bool,
  onClick: PropTypes.func,
  result: PropTypes.object,
  selected: PropTypes.bool,
  setActiveResult: PropTypes.func,
  showDetails: PropTypes.bool,
  actions: PropTypes.any,
  onRequestAccess: PropTypes.func,
};

ResultItem.defaultProps = {
  showDetails: true,
};

export default ResultItem;
